import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  Popover,
  Typography,
} from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { CalendarPlus, MoonStar, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ZoneWorkingHoursDto } from '../../api/workingHours';
import ScheduleCard, { ExceptionChips } from './ScheduleCard';
import type { ScheduleExceptionDraft, ScheduleGroup } from './workingHoursState';
import {
  addDraftException as addDraftExceptionMutation,
  addInterval,
  addWeekendCard,
  assignDay,
  assignException,
  canAddWeekendException,
  deleteCard,
  dropExceptionToNewInterval,
  getExceptionIntervalTargets,
  isAllDayRange,
  normalizeExceptionTarget,
  removeException,
  resetToDefaultSchedule,
  toggleAllDay,
  updateGroup,
  type CardTarget,
  type ExceptionDraftTarget,
  type WorkingHoursEditorState,
} from './workingHoursState';
import { deriveWorkingHoursEditorView, formatDateRu } from './workingHoursView';
import { normalizeExceptionsWithGroups, rowToExceptionDrafts } from './draftModel';
import { extractApiError } from '../../utils/apiError';

// ── Drag hook ─────────────────────────────────────────────────────────────────

function useDragExceptions(
  state: WorkingHoursEditorState,
  onStateChange: (s: WorkingHoursEditorState) => void,
) {
  const [dragTarget, setDragTarget] = useState<CardTarget | 'new' | null>(null);
  const [isDraggingException, setIsDraggingException] = useState(false);
  const dragImageRef = useRef<HTMLElement | null>(null);

  function beginDropZone(e: React.DragEvent<HTMLElement>, target: CardTarget | 'new') {
    e.preventDefault();
    setDragTarget(target);
  }

  function clearDropZone() {
    setDragTarget(null);
  }

  function handleExceptionDragEnd() {
    if (dragImageRef.current) {
      dragImageRef.current.remove();
      dragImageRef.current = null;
    }
    setIsDraggingException(false);
    setDragTarget(null);
  }

  function handleDropToPeriod(e: React.DragEvent<HTMLElement>, target: CardTarget) {
    e.preventDefault();
    const exceptionId = e.dataTransfer.getData('text/exception-id');
    handleExceptionDragEnd();
    if (!exceptionId) return;
    onStateChange(assignException(state, exceptionId, target));
  }

  function handleDropToNewPeriod(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    const exceptionId = e.dataTransfer.getData('text/exception-id');
    handleExceptionDragEnd();
    if (!exceptionId) return;
    onStateChange(dropExceptionToNewInterval(state, exceptionId));
  }

  function handleExceptionDragStart(e: React.DragEvent<HTMLElement>, exceptionId: string) {
    if (dragImageRef.current) {
      dragImageRef.current.remove();
      dragImageRef.current = null;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/exception-id', exceptionId);

    const source = e.currentTarget as HTMLElement;
    const rect = source.getBoundingClientRect();
    const proxy = source.cloneNode(true) as HTMLElement;
    proxy.style.position = 'fixed';
    proxy.style.left = '-1000px';
    proxy.style.top = '-1000px';
    proxy.style.pointerEvents = 'none';
    proxy.style.margin = '0';
    proxy.style.width = `${Math.max(1, Math.round(rect.width))}px`;
    proxy.style.height = `${Math.max(1, Math.round(rect.height))}px`;
    proxy.style.transform = 'none';
    proxy.style.borderRadius = '999px';
    document.body.appendChild(proxy);
    dragImageRef.current = proxy;
    e.dataTransfer.setDragImage(proxy, rect.width / 2, rect.height / 2);
  }

  function handleExceptionDrag() {
    if (!isDraggingException) setIsDraggingException(true);
  }

  return {
    dragTarget,
    isDraggingException,
    beginDropZone,
    clearDropZone,
    handleExceptionDragStart,
    handleExceptionDrag,
    handleExceptionDragEnd,
    handleDropToPeriod,
    handleDropToNewPeriod,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  initialGroups: ScheduleGroup[];
  exceptionRows: ZoneWorkingHoursDto[];
  today: string;
  onClose: () => void;
  onSaveAll: (groups: ScheduleGroup[], exceptions: ScheduleExceptionDraft[]) => Promise<void>;
}

const ScheduleDialog: React.FC<Props> = ({
  open,
  initialGroups,
  exceptionRows,
  today,
  onClose,
  onSaveAll,
}) => {
  const [editorState, setEditorState] = useState<WorkingHoursEditorState>({
    groups: [],
    exceptions: [],
    forceWeekendCard: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [excAnchorEl, setExcAnchorEl] = useState<HTMLElement | null>(null);
  const [excDate, setExcDate] = useState('');
  const [excTargetLocalId, setExcTargetLocalId] = useState<ExceptionDraftTarget | null>(null);

  const excFormOpen = Boolean(excAnchorEl);
  const intervalTargets = getExceptionIntervalTargets(editorState.groups);
  const allowWeekendException = canAddWeekendException(editorState.groups);

  const derived = deriveWorkingHoursEditorView(editorState);
  const drag = useDragExceptions(editorState, setEditorState);

  useEffect(() => {
    if (!open) return;
    const hydrated = rowToExceptionDrafts(exceptionRows, [...initialGroups]);
    setEditorState({
      groups: hydrated.groups,
      exceptions: hydrated.exceptions,
      forceWeekendCard: false,
    });
    setError(null);
    setExcAnchorEl(null);
    setExcDate('');
    setExcTargetLocalId(null);
  }, [open, initialGroups, exceptionRows]);

  useEffect(() => {
    if (!excFormOpen) return;
    const normalized = normalizeExceptionTarget(excTargetLocalId, editorState.groups);
    if (normalized !== excTargetLocalId) setExcTargetLocalId(normalized);
  }, [editorState.groups, excTargetLocalId, excFormOpen]);

  function apply(next: WorkingHoursEditorState) {
    setEditorState(next);
  }

  function closeExceptionForm() {
    setExcAnchorEl(null);
    setExcDate('');
    setExcTargetLocalId(null);
  }

  function addDraftException() {
    if (!excDate || !excTargetLocalId) return;
    const result = addDraftExceptionMutation(
      { groups: editorState.groups, exceptions: editorState.exceptions },
      excDate,
      excTargetLocalId,
    );
    if (result.error) {
      setError(result.error);
      return;
    }
    setEditorState({
      ...editorState,
      groups: result.state.groups,
      exceptions: result.state.exceptions,
    });
    closeExceptionForm();
    setError(null);
  }

  async function handleSaveAll() {
    setSaving(true);
    setError(null);
    try {
      await onSaveAll(
        editorState.groups,
        normalizeExceptionsWithGroups(editorState.groups, editorState.exceptions),
      );
      onClose();
    } catch (err) {
      setError(extractApiError(err, 'Не удалось сохранить.'));
    } finally {
      setSaving(false);
    }
  }

  // ── Exception target chip styles (kept as MUI sx — inside Popover) ──────────
  const excChipUnselected = { borderColor: 'divider', bgcolor: 'transparent', color: 'text.secondary', fontWeight: 500 };
  const excChipBlue   = { borderColor: 'rgba(25, 118, 210, 0.45)', bgcolor: 'rgba(25, 118, 210, 0.14)', color: 'primary.dark',  fontWeight: 600 };
  const excChipGrey   = { borderColor: 'rgba(96, 125, 139, 0.38)', bgcolor: 'rgba(96, 125, 139, 0.15)', color: 'text.primary',  fontWeight: 600 };
  const excChipNewInt = { borderColor: 'rgba(25, 118, 210, 0.45)', bgcolor: 'rgba(25, 118, 210, 0.08)', color: 'primary.dark',  fontWeight: 600 };
  const excChipSx = (selected: boolean, sel = excChipBlue) =>
    ({ height: 24, cursor: 'pointer', ...(selected ? sel : excChipUnselected) } as const);

  function renderSummaryCard(title: string, subtitle: string) {
    return (
      <div className="rounded-lg border border-dashed border-border px-3 py-2.5 bg-card">
        <p className="text-[13px] font-medium text-foreground">{title}</p>
        <p className="mt-0.5 text-[12px] text-muted-foreground">{subtitle}</p>
        <button
          type="button"
          className="mt-2 inline-flex h-6 items-center gap-1 rounded-full border border-border px-2 text-[11px] font-medium text-foreground hover:bg-muted transition-colors"
          onClick={() => apply(resetToDefaultSchedule(editorState))}
        >
          Задать расписание
        </button>
        <ExceptionChips
          list={derived.offExceptions}
          mt={0.85}
          onExceptionDragStart={drag.handleExceptionDragStart}
          onExceptionDrag={drag.handleExceptionDrag}
          onExceptionDragEnd={drag.handleExceptionDragEnd}
          onRemoveException={(id) => apply(removeException(editorState, id))}
        />
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'rounded-2xl border border-border bg-card text-card-foreground shadow-xl',
        elevation: 0,
      }}
    >
      {/* Content */}
      <div className="p-6 pb-0 overflow-y-auto">
        <div className="flex flex-col gap-3">

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div className="flex items-start justify-between gap-4 pb-4">
            <div>
              <h3 className="text-[1.15rem] font-semibold leading-tight text-card-foreground">
                График работы
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">Дни и часы по категориям; исключения — отдельными датами.</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button
                type="button"
                className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                onClick={(e) => { setExcAnchorEl(e.currentTarget); setExcTargetLocalId(null); }}
              >
                <CalendarPlus className="size-3.5 text-muted-foreground" />
                Исключение
              </button>
              <button
                type="button"
                className={`inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors ${
                  drag.dragTarget === 'new'
                    ? 'bg-primary/10 text-primary ring-1 ring-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                onClick={() => apply(addInterval(editorState))}
                onDragOver={(e) => drag.beginDropZone(e, 'new')}
                onDragLeave={drag.clearDropZone}
                onDrop={drag.handleDropToNewPeriod}
              >
                <Plus className="size-3.5 text-muted-foreground" />
                Новый интервал
              </button>
              {!derived.showWeekendCard && (
                <button
                  type="button"
                  className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  onClick={() => apply(addWeekendCard(editorState))}
                >
                  <MoonStar className="size-3.5 text-muted-foreground" />
                  Выходные
                </button>
              )}
            </div>
          </div>

          {/* ── Summary cards ───────────────────────────────────────────────── */}
          {derived.renderMode === 'summary_24x7' && renderSummaryCard('Круглосуточно', '24 часа каждый день')}
          {derived.renderMode === 'summary_closed' && renderSummaryCard('Закрыто', 'Все дни выходные.')}

          {/* ── Interval cards ──────────────────────────────────────────────── */}
          {editorState.groups.map(g => {
            if (derived.renderMode !== 'cards') return null;
            const rowExceptions = editorState.exceptions.filter(ex => ex.periodLocalId === g.localId);
            return (
              <ScheduleCard
                key={g.localId}
                target={g.localId}
                dragTarget={drag.dragTarget}
                variant="interval"
                dayOwners={derived.dayOwners}
                exceptions={rowExceptions}
                interval={{
                  group: g,
                  allDay: isAllDayRange(g.openTime, g.closeTime),
                  onOpenTimeChange: (time) => apply(updateGroup(editorState, g.localId, { openTime: time })),
                  onCloseTimeChange: (time) => apply(updateGroup(editorState, g.localId, { closeTime: time })),
                  onToggleAllDay: () => apply(toggleAllDay(editorState, g.localId)),
                }}
                onAssignDay={(day, target) => apply(assignDay(editorState, day, target))}
                onDeleteCard={(target) => apply(deleteCard(editorState, target))}
                onExceptionDragStart={drag.handleExceptionDragStart}
                onExceptionDrag={drag.handleExceptionDrag}
                onExceptionDragEnd={drag.handleExceptionDragEnd}
                onRemoveException={(id) => apply(removeException(editorState, id))}
                onBeginDrop={drag.beginDropZone}
                onClearDrop={drag.clearDropZone}
                onDropToPeriod={drag.handleDropToPeriod}
              />
            );
          })}

          {/* ── Weekend card ────────────────────────────────────────────────── */}
          {(drag.isDraggingException || (derived.renderMode === 'cards' && derived.showWeekendCard)) && (
            <ScheduleCard
              target="off"
              dragTarget={drag.dragTarget}
              variant="weekend"
              dayOwners={derived.dayOwners}
              exceptions={derived.offExceptions}
              onAssignDay={(day, target) => apply(assignDay(editorState, day, target))}
              onDeleteCard={(target) => apply(deleteCard(editorState, target))}
              onExceptionDragStart={drag.handleExceptionDragStart}
              onExceptionDrag={drag.handleExceptionDrag}
              onExceptionDragEnd={drag.handleExceptionDragEnd}
              onRemoveException={(id) => apply(removeException(editorState, id))}
              onBeginDrop={drag.beginDropZone}
              onClearDrop={drag.clearDropZone}
              onDropToPeriod={drag.handleDropToPeriod}
            />
          )}

          {/* ── Exception popover — kept as MUI Popover for positioning ──────── */}
          <Popover
            open={excFormOpen}
            anchorEl={excAnchorEl}
            onClose={() => setExcAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 1.25, minWidth: 300 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Новое исключение
              </Typography>

              <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                <Chip
                  icon={<EventOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={excDate ? formatDateRu(excDate) : 'Выбрать дату'}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
                <Box
                  component="input"
                  type="date"
                  value={excDate}
                  min={today}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExcDate(e.target.value)}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      margin: 0,
                      cursor: 'pointer',
                    },
                  }}
                />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Режим дня
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {intervalTargets.map(g => (
                  <Chip
                    key={g.localId}
                    label={`${g.openTime}–${g.closeTime}`}
                    size="small"
                    variant="outlined"
                    onClick={() => setExcTargetLocalId(g.localId)}
                    sx={excChipSx(excTargetLocalId === g.localId)}
                  />
                ))}
                {allowWeekendException && (
                  <Chip
                    label="Выходной"
                    size="small"
                    variant="outlined"
                    onClick={() => setExcTargetLocalId('off')}
                    sx={excChipSx(excTargetLocalId === 'off', excChipGrey)}
                  />
                )}
                <Chip
                  label="Новый интервал"
                  size="small"
                  variant="outlined"
                  onClick={() => setExcTargetLocalId('new')}
                  sx={{ ...excChipSx(excTargetLocalId === 'new', excChipNewInt), borderStyle: 'dashed' }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Button size="sm" variant="ghost" onClick={() => setExcAnchorEl(null)}>
                  Отмена
                </Button>
                <Button size="sm" onClick={addDraftException} disabled={!excDate || !excTargetLocalId}>
                  Добавить
                </Button>
              </Box>
            </Box>
          </Popover>

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Actions footer */}
      <div className="flex justify-end gap-2 px-6 py-4">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Отмена
        </Button>
        <Button onClick={handleSaveAll} disabled={saving}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </div>
    </Dialog>
  );
};

export default ScheduleDialog;
